import React from 'react';
import { Shield } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const PrivacyPolicy: React.FC = () => {
  return (
    <article className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead
        title="Politique de Confidentialité"
        description="Politique de confidentialité de Doux ce Moman : collecte et traitement des données personnelles, cookies, droits RGPD."
        path="/privacy-policy"
        noindex
      />
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8">
        {/* Header */}
        <header className="flex items-center gap-3 mb-8">
          <Shield className="text-brand" size={32} />
          <h1 className="text-3xl font-bold text-gray-900 font-playfair">
            Politique de Confidentialité
          </h1>
        </header>

        <div className="prose prose-lg max-w-none">
          <div className="mb-6 text-sm text-gray-600">
            <p><strong>www.douxcemoman.fr</strong></p>
            <p>Doux ce Moman SAS</p>
            <p>N° SIRET : 99087394500010</p>
            <p>Type de site : boutique en ligne</p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Le but de cette politique de confidentialité</h2>
            <p className="text-gray-700 mb-4">
              Le but de cette politique de confidentialité est d'informer les utilisateurs de notre site des données
              personnelles que nous recueillons ainsi que des informations suivantes, le cas échéant :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Les données personnelles que nous recueillerons</li>
              <li>L'utilisation des données recueillies</li>
              <li>Qui a accès aux données recueillies</li>
              <li>Les droits des utilisateurs du site</li>
              <li>La politique de cookies du site</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Cette politique de confidentialité s'applique en complément des conditions générales d'utilisation de notre site.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Lois applicables</h2>
            <p className="text-gray-700 mb-4">
              Conformément au Règlement général sur la protection des données (RGPD), cette politique de
              confidentialité est conforme aux normes suivantes.
            </p>
            <p className="text-gray-700 mb-2">Les données à caractère personnel doivent être :</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>traitées de manière licite, loyale et transparente au regard de la personne concernée (licéité, loyauté, transparence) ;</li>
              <li>collectées pour des finalités déterminées, explicites et légitimes, et ne pas être traitées ultérieurement d'une manière incompatible avec ces finalités ;</li>
              <li>adéquates, pertinentes et limitées à ce qui est nécessaire au regard des finalités pour lesquelles elles sont traitées (minimisation des données) ;</li>
              <li>exactes et, si nécessaire, tenues à jour ;</li>
              <li>conservées sous une forme permettant l'identification des personnes concernées pendant une durée n'excédant pas celle nécessaire au regard des finalités pour lesquelles elles sont traitées ;</li>
              <li>traitées de façon à garantir une sécurité appropriée des données à caractère personnel.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Consentement</h2>
            <p className="text-gray-700">
              Les utilisateurs conviennent qu'en utilisant notre site, ils acceptent :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>les conditions énoncées dans la présente politique de confidentialité et</li>
              <li>la collecte, l'utilisation et la conservation des données énumérées dans la présente politique.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Données personnelles que nous collectons</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Données collectées automatiquement</h3>
            <p className="text-gray-700 mb-4">
              Nous ne collectons aucune donnée automatiquement sur notre site.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Données recueillies de façon non automatique</h3>
            <p className="text-gray-700 mb-2">
              Nous pouvons également collecter les données suivantes lorsque vous effectuez certaines fonctions sur notre site :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Prénom et nom</li>
              <li>Date de naissance</li>
              <li>Email</li>
              <li>Numéro de téléphone</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Ces données peuvent être recueillies au moyen des méthodes suivantes :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Prise de rendez-vous</li>
              <li>Achat de bon cadeau</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Veuillez noter que nous ne collectons que les données qui nous aident à atteindre l'objectif énoncé
              dans cette politique de confidentialité. Nous ne recueillerons pas de données supplémentaires sans
              vous en informer au préalable.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Comment nous utilisons les données personnelles</h2>
            <p className="text-gray-700 mb-4">
              Les données personnelles recueillies sur notre site seront utilisées uniquement aux fins précisées
              dans la présente politique ou indiquées sur les pages pertinentes de notre site. Nous n'utiliserons pas
              vos données au-delà de ce que nous divulguerons.
            </p>
            <p className="text-gray-700">
              Les données que nous recueillons lorsque l'utilisateur exécute certaines fonctions peuvent être
              utilisées aux fins suivantes :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-2">
              <li>Réservation en ligne</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Avec qui nous partageons les données personnelles</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3">Employés</h3>
            <p className="text-gray-700 mb-4">
              Nous pouvons divulguer à tout membre de notre organisation les données utilisateur dont ce
              membre a raisonnablement besoin pour réaliser les objectifs énoncés dans la présente politique.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">Tierces parties</h3>
            <p className="text-gray-700 mb-2">
              Nous pouvons partager les données utilisateur avec les tiers suivants :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Traitement des paiements</li>
              <li>Analyse d'audience</li>
            </ul>
            <p className="text-gray-700 mt-4 mb-2">
              Nous pouvons partager les données utilisateur avec des tiers aux fins suivantes :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Données de paiement</li>
              <li>Données de navigation</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Les tiers ne seront pas en mesure d'accéder aux données des utilisateurs au-delà de ce qui est
              raisonnablement nécessaire pour atteindre l'objectif donné.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">Autres divulgations</h3>
            <p className="text-gray-700 mb-2">
              Nous nous engageons à ne pas vendre ou partager vos données avec d'autres tiers, sauf dans les cas suivants :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>si la loi l'exige</li>
              <li>si elle est requise pour toute procédure judiciaire</li>
              <li>pour prouver ou protéger nos droits légaux</li>
              <li>à des acheteurs ou des acheteurs potentiels de cette société dans le cas où nous cherchons à la vendre</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Si vous consultez des hyperliens de notre site vers un autre site, veuillez noter que nous ne sommes
              pas responsables et n'avons pas de contrôle sur leurs politiques et pratiques de confidentialité.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Combien de temps nous stockons les données personnelles</h2>
            <p className="text-gray-700">
              Les données personnelles sont conservées aussi longtemps que nécessaire pour les fins pour
              lesquelles elles ont été collectées, ou pour se conformer à nos obligations légales. Une fois cette
              durée écoulée, les données seront supprimées de manière sécurisée.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Comment nous protégeons vos données personnelles</h2>
            <p className="text-gray-700 mb-4">
              Nous prenons la sécurité de vos données très au sérieux. Nous avons mis en place des mesures
              techniques pour garantir que vos informations personnelles sont protégées contre tout accès,
              modification ou divulgation non autorisé(e).
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3">1. Sécurité des Communications</h3>
            <p className="text-gray-700 mb-2">
              Toutes les données que vous échangez avec notre site sont protégées de manière maximale.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Chiffrement (HTTPS)</strong> : Nous utilisons un chiffrement de pointe (SSL/TLS) pour sécuriser la
              connexion entre votre appareil et nos serveurs. Cela garantit que les informations sont illisibles
              pendant leur transit sur Internet. Notre site est obligatoirement accessible via HTTPS.</li>
              <li><strong>Protection des Accès</strong> : Les flux de communication avec nos systèmes internes sont strictement
              chiffrés et surveillés.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">2. Contrôle d'Accès et Protection des Données</h3>
            <p className="text-gray-700 mb-2">
              Nous nous assurons que seuls les utilisateurs et les systèmes autorisés peuvent accéder à vos données.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Jetons Sécurisés (Tokens)</strong> : Nous utilisons des jetons d'accès sécurisés pour gérer et valider vos
              sessions. Ces jetons sont gérés de manière isolée pour confirmer votre identité et vos droits d'accès à
              chaque étape.</li>
              <li><strong>Sécurité du Serveur (CORS Strict)</strong> : Notre serveur vérifie la provenance de chaque demande.
              Seules les applications et les domaines que nous avons expressément autorisés sont autorisés à
              communiquer avec nos services. Cela empêche les sites non fiables de manipuler vos données.</li>
            </ul>

            <p className="text-gray-700 mt-4">
              Bien que nous prenions toutes les précautions raisonnables pour sécuriser les données de nos
              utilisateurs et garantir leur protection, il subsiste toujours un certain risque. L'Internet dans son
              ensemble peut parfois présenter des vulnérabilités, et il nous est donc impossible d'assurer une
              sécurité absolue des données des utilisateurs au-delà de ce qui est raisonnablement réalisable.
            </p>
            <p className="text-gray-700 mt-4">
              En cas de violation de la sécurité affectant vos renseignements personnels, nous vous en
              informerons conformément aux obligations légales et prendrons les mesures nécessaires pour en
              limiter les risques.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Mineurs</h2>
            <p className="text-gray-700">
              Le RGPD précise que les personnes de moins de 15 ans sont considérées comme des mineurs aux
              fins de la collecte de données. Les mineurs doivent avoir le consentement d'un représentant légal
              pour que leurs données soient recueillies, traitées et utilisées.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Vos droits en tant qu'utilisateur</h2>
            <p className="text-gray-700 mb-2">
              En vertu du RGPD, les utilisateurs ont les droits suivants sur leurs données en tant que personnes concernées :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>droit d'accès</li>
              <li>droit de rectification</li>
              <li>droit à l'effacement</li>
              <li>droit de restreindre le traitement</li>
              <li>droit à la portabilité des données</li>
              <li>droit d'objection</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Vous trouverez de plus amples informations sur ces droits au chapitre 3 (article 12-23) du RGPD.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Comment modifier, supprimer ou contester les données recueillies</h2>
            <p className="text-gray-700 mb-4">
              Si vous souhaitez que vos renseignements soient supprimés ou modifiés d'une façon ou d'une autre,
              veuillez communiquer avec notre agent de protection des données personnelles ici :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-gray-700"><strong>Marie AMET</strong></p>
              <p className="text-gray-700">contact@douxcemoman.fr</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Politique sur les cookies</h2>
            <p className="text-gray-700 mb-4">
              Un cookie est un petit fichier stocké sur le disque dur d'un utilisateur par un site. Il permet de
              collecter des données concernant les habitudes de navigation et les interactions de l'utilisateur avec
              le site.
            </p>
            <p className="text-gray-700 mb-2">
              Nous utilisons les types de cookies suivants sur notre site :
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Cookies fonctionnels</strong> : Nous les utilisons pour mémoriser toutes les sélections que vous faites sur notre site afin
              qu'elles soient sauvegardées pour vos prochaines visites.</li>
              <li><strong>Cookies analytiques</strong> : Cela nous permet d'améliorer la conception et la fonctionnalité de notre site en recueillant
              des données sur le contenu auquel vous accédez et que vous consultez en utilisant notre site.</li>
            </ul>
            <p className="text-gray-700 mt-4">
              Vous pouvez choisir d'être averti chaque fois qu'un cookie est transmis ou de désactiver
              complètement les cookies en modifiant les paramètres de votre navigateur Internet. Cependant,
              veuillez noter que cela pourrait affecter certaines fonctionnalités du site et diminuer la qualité de
              votre expérience utilisateur.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Dispositions supplémentaires</h2>
            <p className="text-gray-700">
              <strong>Gestion du Questionnaire d'Éligibilité</strong> : L'unique finalité est de vérifier votre éligibilité pour
              des raisons de sécurité. Les réponses ne sont pas conservées sur nos systèmes ou nos bases de
              données.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Modifications</h2>
            <p className="text-gray-700">
              Cette politique de confidentialité peut être modifiée de temps à autre afin de maintenir la
              conformité avec la loi et de tenir compte de tout changement à notre processus de collecte de
              données. Nous recommandons à nos utilisateurs de vérifier notre politique de confidentialité de
              temps à autre pour s'assurer qu'ils soient informés de toute mise à jour. Au besoin, nous pouvons
              informer les utilisateurs par courriel des changements apportés à cette politique.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 font-playfair">Contact</h2>
            <p className="text-gray-700 mb-2">
              Si vous avez des questions à nous poser, n'hésitez pas à communiquer avec nous en utilisant ce qui suit :
            </p>
            <div className="bg-gray-50 p-4 rounded-lg space-y-1">
              <p className="text-gray-700">06 28 97 99 07</p>
              <p className="text-gray-700">contact@douxcemoman.fr</p>
              <p className="text-gray-700">32 Rue Lyautey, 25870 Geneuille, France</p>
            </div>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200 text-sm text-gray-600">
            <p>Date d'entrée en vigueur de la présente politique : le 6 octobre 2025</p>
            <p className="mt-2">© 2002-2025, DocumentsLégaux™ (Sequiter Inc.)</p>
          </div>
        </div>
      </div>
    </article>
  );
};

export default PrivacyPolicy;
